/**
 * Dispositivos pendentes de vínculo (cliente/unidade/área).
 *
 * Órfãos nunca ficam escondidos: aparecem aqui com os dados legados
 * (NomeCliente, CNPJ, Filial, Setor), o motivo e a sugestão inequívoca
 * quando existir. O vínculo (individual ou em lote) é validado no backend.
 */
import { useEffect, useMemo, useState } from "react";
import { Link2, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { apiErrorMessage, inputCls, typeLabel, useHierarchy } from "./hierarchy";

export default function PendingLinks() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [target, setTarget] = useState({ client_id: "", unit_id: "", area_id: "", subarea_id: "" });
  const { clients, units, areas, subareas } = useHierarchy({
    clientId: target.client_id,
    unitId: target.unit_id,
    areaId: target.area_id,
  });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/device-monitoring/links/pending", {
        params: { q: q || undefined, limit: 1000 },
      });
      setRows(data.items || []);
      setTotal(data.total || 0);
      setSelected(new Set());
    } catch (err) {
      setRows([]);
      toast.error(apiErrorMessage(err, "Erro ao carregar pendências de vínculo."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const allChecked = rows.length > 0 && selected.size === rows.length;
  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const applySuggestion = (row) => {
    setTarget({
      client_id: row.suggested_client_id || "",
      unit_id: row.suggested_unit_id || "",
      area_id: "",
      subarea_id: "",
    });
    // seleciona todos com a mesma sugestão (lote natural)
    setSelected(
      new Set(
        rows
          .filter(
            (r) =>
              r.suggested_client_id === row.suggested_client_id &&
              r.suggested_unit_id === row.suggested_unit_id
          )
          .map((r) => r.id)
      )
    );
  };

  const setLevel = (k) => (e) => {
    const reset = {
      client_id: { unit_id: "", area_id: "", subarea_id: "" },
      unit_id: { area_id: "", subarea_id: "" },
      area_id: { subarea_id: "" },
    }[k] || {};
    setTarget({ ...target, [k]: e.target.value, ...reset });
  };

  const canSave =
    selected.size > 0 && target.client_id && target.unit_id && target.area_id && !saving;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/device-monitoring/links/assign", {
        device_ids: [...selected],
        client_id: target.client_id,
        unit_id: target.unit_id,
        area_id: target.area_id,
        subarea_id: target.subarea_id || null,
      });
      if (data.linked) toast.success(`${data.linked} dispositivo(s) vinculado(s).`);
      if (data.errors?.length) {
        toast.error(
          `${data.errors.length} não vinculado(s): ` +
            data.errors
              .slice(0, 5)
              .map((e) => `${e.code || e.device_id}: ${e.detail}`)
              .join("; ")
        );
      }
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Erro ao salvar vínculo."));
    } finally {
      setSaving(false);
    }
  };

  const reconcile = async () => {
    try {
      const { data } = await api.post("/device-monitoring/links/reconcile");
      toast.success(
        `Reconciliação: ${data.auto_fixed} corrigido(s) automaticamente, ${data.pending} pendente(s).`
      );
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Erro na reconciliação."));
    }
  };

  const groups = useMemo(() => {
    const out = {};
    for (const r of rows) out[r.reason_label || r.reason] = (out[r.reason_label || r.reason] || 0) + 1;
    return out;
  }, [rows]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-semibold">Dispositivos pendentes de vínculo</h3>
        <span className="text-sm text-muted-foreground">({total})</span>
        <form
          className="flex items-center gap-2 ml-auto"
          onSubmit={(e) => {
            e.preventDefault();
            load();
          }}
        >
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Código, cliente legado, CNPJ…"
              className="pl-9 pr-3 py-2 rounded-md border border-border bg-background text-sm"
            />
          </div>
          <button type="submit" className="px-3 py-2 bg-secondary rounded-md text-sm">
            Buscar
          </button>
          <button
            type="button"
            onClick={reconcile}
            className="flex items-center gap-1.5 px-3 py-2 bg-secondary rounded-md text-sm"
            title="Reexecuta a reconciliação automática (somente correspondências inequívocas)"
          >
            <RefreshCw className="h-4 w-4" /> Reconciliar
          </button>
        </form>
      </div>

      {Object.keys(groups).length > 0 && (
        <div className="flex flex-wrap gap-2 text-xs">
          {Object.entries(groups).map(([label, n]) => (
            <span key={label} className="px-2 py-1 rounded bg-amber-500/10 text-amber-700 dark:text-amber-400">
              {label}: {n}
            </span>
          ))}
        </div>
      )}

      <div className="rounded-lg border border-border bg-card p-4 space-y-3">
        <div className="text-sm font-medium">
          Vincular {selected.size} selecionado(s) a:
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
          <select value={target.client_id} onChange={setLevel("client_id")} className={inputCls}>
            <option value="">Cliente…</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <select value={target.unit_id} onChange={setLevel("unit_id")} disabled={!target.client_id} className={inputCls}>
            <option value="">Unidade…</option>
            {units.map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
          <select value={target.area_id} onChange={setLevel("area_id")} disabled={!target.unit_id} className={inputCls}>
            <option value="">Área…</option>
            {areas.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
          <select
            value={target.subarea_id}
            onChange={setLevel("subarea_id")}
            disabled={!target.area_id || subareas.length === 0}
            className={inputCls}
          >
            <option value="">Subárea (opcional)</option>
            {subareas.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          <button
            disabled={!canSave}
            onClick={save}
            data-testid="links-save"
            className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium disabled:opacity-50"
          >
            <Link2 className="h-4 w-4" />
            {saving ? "Salvando…" : "Salvar vínculo"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          Nenhum dispositivo pendente de vínculo.
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-auto max-h-[32rem]">
          <table className="w-full text-xs">
            <thead className="bg-muted/50 text-muted-foreground sticky top-0">
              <tr>
                <th className="py-2 px-2">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={() => setSelected(allChecked ? new Set() : new Set(rows.map((r) => r.id)))}
                  />
                </th>
                <th className="text-left py-2 px-2">Código</th>
                <th className="text-left py-2 px-2">Número</th>
                <th className="text-left py-2 px-2">Tipo</th>
                <th className="text-left py-2 px-2">NomeCliente legado</th>
                <th className="text-left py-2 px-2">CNPJ</th>
                <th className="text-left py-2 px-2">Filial</th>
                <th className="text-left py-2 px-2">Área/Setor</th>
                <th className="text-left py-2 px-2">Origem</th>
                <th className="text-left py-2 px-2">Motivo</th>
                <th className="text-left py-2 px-2">Cliente sugerido</th>
                <th className="text-left py-2 px-2">Unidade sugerida</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-border/50">
                  <td className="py-1.5 px-2 text-center">
                    <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} />
                  </td>
                  <td className="py-1.5 px-2 font-mono">{r.code}</td>
                  <td className="py-1.5 px-2 font-mono">{r.number || "—"}</td>
                  <td className="py-1.5 px-2">{typeLabel(r.type_key)}</td>
                  <td className="py-1.5 px-2">{r.legacy_client_name || "—"}</td>
                  <td className="py-1.5 px-2">{r.legacy_client_cnpj || "—"}</td>
                  <td className="py-1.5 px-2">{r.legacy_branch || "—"}</td>
                  <td className="py-1.5 px-2">{r.legacy_area || r.location || "—"}</td>
                  <td className="py-1.5 px-2">{r.origin}</td>
                  <td className="py-1.5 px-2">{r.reason_label}</td>
                  <td className="py-1.5 px-2">
                    {r.suggested_client_name ? (
                      <button className="underline" onClick={() => applySuggestion(r)}>
                        {r.suggested_client_name}
                      </button>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="py-1.5 px-2">{r.suggested_unit_name || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
